import { useEffect, useState } from "react";
import { Bookmark, BookmarkCheck, Loader2 } from "lucide-react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { isDiscoverySaved, toggleSaveDiscovery } from "@/lib/account.functions";
import { useLocale } from "@/hooks/use-locale";
import { curatedUiTranslation } from "@/lib/curated-ui-translations";

export function SaveButton({ discoveryId }: { discoveryId: string }) {
  const [lang] = useLocale();
  const [signedIn, setSignedIn] = useState(false);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(false);
  const check = useServerFn(isDiscoverySaved);
  const toggle = useServerFn(toggleSaveDiscovery);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase.auth.getSession();
      const isIn = !!data.session;
      if (!active) return;
      setSignedIn(isIn);
      if (!isIn) { setChecked(true); return; }
      try {
        const res = await check({ data: { discoveryId } });
        if (active) setSaved(res.saved);
      } catch { /* ignore */ }
      if (active) setChecked(true);
    })();
    return () => { active = false; };
  }, [discoveryId, check]);

  async function onClick() {
    if (!signedIn) return;
    setBusy(true);
    try {
      const res = await toggle({ data: { discoveryId } });
      setSaved(res.saved);
    } finally { setBusy(false); }
  }

  if (!signedIn) {
    return null;
  }


  return (
    <button
      onClick={onClick}
      disabled={busy || !checked}
      aria-pressed={saved}
      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider transition ${
        saved
          ? "bg-primary text-primary-foreground hover:brightness-110"
          : "border border-border bg-card text-foreground hover:border-primary"
      }`}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
      {curatedUiTranslation(lang, saved ? "Saved" : "Save") ?? (saved ? "Saved" : "Save")}
    </button>
  );
}
